import { BabylonjsModule } from '@modules/babylonjs/src/BabylonjsModule';
import { Layers } from '@modules/core';
import { useZed } from '@modules/core/src/react/useZed';
import { StandardModule } from '@modules/standard/src/StandardModule';
import { zams } from '@zams/logger/zed/generated/loggerMap';
import { useEffect, useMemo, useRef, useState } from 'react';

import { MeshType } from '../ecs/component/data/DanjiMesh';
import { RoomTypeResponse } from '../ecs/component/response/RoomType';
import { getTourDanjiMetaData, TourDanjiModule } from '../TourDanjiModule';

import { AreaUnitChangeButton } from './AreaUnitChangeButton';
import arrow_icon from './assets/arrow_down.png';
import { CompassPanel } from './CompassPanel';
import { useScreen } from './hooks/useScreen';
import styles from './styles/RoomTypeFilter.module.css';


export const RoomTypeFilter = () => {
  const [roomTypes, setRoomTypes] = useState<RoomTypeResponse[]>([]);
  const roomTypesRef = useRef(roomTypes);
  const [isAll, setIsAll] = useState(false);
  const [isFold, setIsFold] = useState(true);
  const [isVisible, setIsVisible] = useState(true);
  const isFoldRef = useRef(isFold);
  const [useMeterAreaUnit, setMeterAreaUnit] = useState(true);
  const listRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [listContainerWidth, setListContainerWidth] = useState(0);
  const listContainerWidthRef = useRef(listContainerWidth);
  const screenInfo = useScreen();

  const zed = useZed<[BabylonjsModule, StandardModule, TourDanjiModule]>();
  
  const checkUIContentsOverflow = (isFolding: boolean) => {
    if (!listRef.current) return;
    if (isFolding) {
      setIsOverflowing(listRef.current.scrollWidth > listRef.current.clientWidth);
    } else {
      setIsOverflowing(listRef.current.scrollHeight > 50);
    }
    adjustParentHeight();
  };

  const { isDesktop } = screenInfo;

  function adjustParentHeight() {
    var parent = document.getElementById('backgroundFrame');
    var rectangle = document.getElementById('listContainer');
    if (parent && rectangle) {
      parent.style.height = (rectangle.offsetHeight + 10) + 'px';
    }
  }

  useEffect(() => {
    listContainerWidthRef.current = listContainerWidth;
  }, [listContainerWidth]);

  useEffect(() => {
    roomTypesRef.current = roomTypes;
  }, [roomTypes]);

  useEffect(() => {
    if (!isDesktop) return;
    const scrollContainer = listRef.current;
    if (!scrollContainer) return;

    let isDown = false;
    let startX: number;
    let scrollLeft: number;

    const handleMouseDown = (e: MouseEvent) => {
      isDown = true;
      scrollContainer.classList.add(styles.active);
      startX = e.pageX - scrollContainer.offsetLeft;
      scrollLeft = scrollContainer.scrollLeft;
    };

    const handleMouseLeave = () => {
      isDown = false;
      scrollContainer.classList.remove(styles.active);
    };

    const handleMouseUp = () => {
      isDown = false;
      scrollContainer.classList.remove(styles.active);
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - scrollContainer.offsetLeft;
      const walk = (x - startX) * 2; // 스크롤 속도 조절
      scrollContainer.scrollLeft = scrollLeft - walk;
    };

    scrollContainer.addEventListener('mousedown', handleMouseDown);
    scrollContainer.addEventListener('mouseleave', handleMouseLeave);
    scrollContainer.addEventListener('mouseup', handleMouseUp);
    scrollContainer.addEventListener('mousemove', handleMouseMove);

    return () => {
      scrollContainer.removeEventListener('mousedown', handleMouseDown);
      scrollContainer.removeEventListener('mouseleave', handleMouseLeave);
      scrollContainer.removeEventListener('mouseup', handleMouseUp);
      scrollContainer.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  useEffect(() => {    
    const timerId = setInterval(() => {
      if (!listRef || !listRef.current) return;
      if (listRef.current.scrollWidth !== listContainerWidthRef.current) {
        checkUIContentsOverflow(isFoldRef.current);
      }
      setListContainerWidth(listRef.current.scrollWidth);
    }, 500);
  
    return () => {
      clearInterval(timerId);
    };
  }, []);

  useEffect(() => {
    checkUIContentsOverflow(isFold);
    isFoldRef.current = isFold;
  }, [isFold]);

  useEffect(() => {
    if (!zed) return;
    zed.api.observer.onPreLoad.addOnce(() => {        
      zed.api.observer.onCameraViewTypeChanged.add((data) => {
        const isVisible = data !== 'UnitWindowView' && data !== 'FakeUnitWindowView';        
        if (isVisible) {          
          setIsVisible(true);
          if (roomTypesRef.current)
            zed?.api.updateRoomTypeResponse(roomTypesRef.current);
        } else {
          setIsVisible(false);
          zed?.api.setVisibleMeshes(MeshType.DONG_ROOMTYPE_WALL, false);
        }
      })
    });

    zed.api.observer.onUpdateRoomTypeFilter.add((data) => {
      setRoomTypes(data);
    });

  }, [zed]);

  const handleRoomTypeToggle = (index: number) => {
    let newResponses = roomTypes.map((response, idx) => {
      if (idx === index) {
        // 이전 상태 기준으로 켜져 있었으면 off, 꺼져 있었으면 on
        const metaData = getTourDanjiMetaData();
        const nextStatus = isAll || !response.active ? 'on' : 'off';
        zams
          .z133()
          .setAllParams({
            screen_name: metaData.screen_name,
            ...(metaData.deal_type && { deal_type: metaData.deal_type }),
            ...(metaData.item_type && { item_type: metaData.item_type }),
            ...(metaData.ho_id && { ho_id: metaData.ho_id }),
            area_danji_id: metaData.danjiId,
            dong: metaData.dong,
            ho: metaData.ho,
            item_id: metaData.itemId,
            status_value: useMeterAreaUnit ? response.roomTypeTitle.m2 : response.roomTypeTitle.p,
            status: nextStatus,
          })
          .send();

        return { ...response, active: !response.active };
      } else {
        return response;
      }
    });

    setIsAll(!newResponses.some((response) => !response.active));
    zed?.api.updateRoomTypeResponse(newResponses);
  };

  const toggleAll = () => {
    const isOn = !isAll;
    const metaData = getTourDanjiMetaData();
    zams
      .z133()
      .setAllParams({
        screen_name: metaData.screen_name,
        ...(metaData.deal_type && { deal_type: metaData.deal_type }),
        ...(metaData.item_type && { item_type: metaData.item_type }),
        ...(metaData.ho_id && { ho_id: metaData.ho_id }),
        area_danji_id: metaData.danjiId,
        dong: metaData.dong,
        ho: metaData.ho,
        item_id: metaData.itemId,
        status_value: '전체',
        status: isOn ? 'on' : 'off',
      })
      .send();

    setIsAll(isOn);
    const responses = roomTypes.map((response) => {
      return { ...response, active: isOn };
    });
    zed?.api.updateRoomTypeResponse(responses);
    setRoomTypes(responses);
  };

  let backgroundFrame = '';
  let listContainer = '';
  let rightEdgeGradation = '';
  if (isDesktop) {
    backgroundFrame = isFold ? styles.backgroundFrameWWW : styles.backgroundFrameExpandWWW;
    listContainer = isFold ? styles.listContainerWWW : styles.listContainerExpandWWW;
    rightEdgeGradation = styles.rightEdgeGradationWWW;
  } else {
    backgroundFrame = isFold ? styles.backgroundFrame : styles.backgroundFrameExpand;
    listContainer = isFold ? styles.listContainer : styles.listContainerExpand;
    rightEdgeGradation = styles.rightEdgeGradation;
  }  

  return isVisible && (
    <div style={{ zIndex: Layers.UI_CONTENTS }}
      className={styles.root}>
      <CompassPanel />
      <AreaUnitChangeButton
        useMeterAreaUnit = {useMeterAreaUnit}
        onClick={() => {
          const metaData = getTourDanjiMetaData();
          zams
            .z132()
            .setAllParams({
              screen_name: metaData.screen_name,
              ...(metaData.deal_type && { deal_type: metaData.deal_type }),
              ...(metaData.item_type && { item_type: metaData.item_type }),
              ...(metaData.ho_id && { ho_id: metaData.ho_id }),
              area_danji_id: metaData.danjiId,
              dong: metaData.dong,
              ho: metaData.ho,
              item_id: metaData.itemId,
              status_value: useMeterAreaUnit ? '평' : 'm',
            })
            .send();
          setMeterAreaUnit(!useMeterAreaUnit);
        }}/>
      <div 
        id={'backgroundFrame'}
        className={backgroundFrame}
        >        
        <button   
          className={isAll ? styles.selectAllButtonActive : styles.selectAllButton}
          onClick={() => { 
            toggleAll(); 
          }}
        >
          {'전체'}
        </button>
        <div 
          id={'listContainer'}
          ref={listRef}
          className={listContainer}
          >
            {!isFold &&
              <button   
                className={isAll ? styles.selectAllButtonActive : styles.selectAllButton}
                onClick={() => { 
                  toggleAll(); 
                }}>
                {'전체'}
              </button>} 
            {roomTypes.map((response, index) => (
              <button
                key={index}
                className={response.active? styles.roomTypeButtonActive : styles.roomTypeButton}
                onClick={() => {
                    handleRoomTypeToggle(index)
                  }
                }
              > 
                <span className={styles.buttonContentContainer}>
                  <span style={{
                      display: 'inline-block',
                      width: '14px',
                      height: '14px',
                      backgroundColor: response.color,
                      marginRight: '8px',
                      borderRadius: '50%',
                    }}/>
                  <span className={styles.text}>
                    {useMeterAreaUnit ? `${response.roomTypeTitle.m2}㎡` : `${response.roomTypeTitle.p}평` }
                  </span>
                </span>
              </button>
            ))}
            {isFold &&
              <div style={{
                position: 'relative',
                flexShrink: 0,
                width: '44px', height: '30px',
                marginTop: '10px',
              }}/>}

        </div>
        {isOverflowing && 
        <div
          className={rightEdgeGradation}>
            <button
              style={{
                position: 'absolute',
                width: '44px',
                height: '50px',
                right: '0px',
                backgroundColor: 'transparent',
                border: 'none',
                outline: 'none'
              }}
              onClick={() => {
                setIsFold(!isFold);
              }}>
              <img
                src={arrow_icon}
                style={{
                  position: 'absolute',
                  top: 20,
                  right: 11,
                  width: 18,
                  height: 9.4,
                  transform: isFold ? 'scaleY(1)' : 'scaleY(-1)'
                }}/>
            </button>

        </div>
      }
      </div>            
    </div>
  );
};
